import { type ItemSuggestion, type OutboxStatus, parseLocalDate, zonedToUtc } from '@hugo/core'

const DAY_MS = 86_400_000

/** "in 2 days", "3 hours ago" relative to `now` (the user's clock, which may be moved in tests). */
export function relativeTime(date: Date, now: Date, locale = 'en'): string {
  const diff = date.getTime() - now.getTime()
  const abs = Math.abs(diff)
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  // Within 45 s either way reads as "now": absorbs render lag and small clock differences.
  if (abs < 45_000) return rtf.format(0, 'second')
  if (abs < 60_000) return rtf.format(Math.round(diff / 1000), 'second')
  if (abs < 3_600_000) return rtf.format(Math.round(diff / 60_000), 'minute')
  if (abs < DAY_MS) return rtf.format(Math.round(diff / 3_600_000), 'hour')
  return rtf.format(Math.round(diff / DAY_MS), 'day')
}

/** "Sat, 17 Oct 2026, 21:40" in the device's time zone. */
export function formatDateTime(date: Date, locale = 'en-GB'): string {
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

/** Initials for an avatar fallback. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const first = parts[0]?.[0] ?? '?'
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase()
}

const EVENT_LABELS: Record<string, string> = {
  'session.started': 'Signed in',
  'clock.changed': 'Test clock moved',
  'debug.ping': 'Test ping',
  'item.created': 'Item created',
  'item.triaged': 'Filed',
  'item.kind_changed': 'Who waits changed',
  'item.updated': 'Edited',
  'item.due_set': 'Due date changed',
  'item.counterpart_set': 'Person changed',
  'item.snoozed': 'Snoozed',
  'item.woke': 'Back from snooze',
  'item.closed': 'Closed',
  'item.reopened': 'Reopened',
  'item.planned': 'Planned on the board',
  'item.activity': 'New activity',
  'item.suggested': 'hugo suggested',
  'item.suggestion_dismissed': 'Suggestion dismissed',
  'connection.connected': 'Connected',
  'connection.disconnected': 'Disconnected',
  'connection.failed': 'Sync failed',
  'connection.synced': 'Synced',
  'outbox.drafted': 'Draft waiting for approval',
  'outbox.discarded': 'Draft discarded',
  'outbox.withdrawn': 'Draft withdrawn',
  'outbox.sending': 'Sending',
  'outbox.sent': 'Sent',
  'outbox.dry_run': 'Would have been sent',
  'outbox.failed': 'Sending failed',
  'time_off.added': 'Time off added',
  'time_off.removed': 'Time off removed',
  'headsup.dismissed': 'Heads-up handled',
  'person.created': 'Person added',
  'action.undone': 'Undone',
  'settings.changed': 'Settings changed',
}

const KIND_LABELS: Record<string, string> = {
  task: 'My tasks',
  owe: 'I owe',
  waiting: 'Waiting on',
  fyi: 'FYI',
}

export function kindLabel(kind: string | null): string {
  return kind ? (KIND_LABELS[kind] ?? kind) : 'Inbox'
}

/** A triage choice as a place: "I owe", "My tasks", "FYI"; ignoring is "Ignore". */
export function choiceLabel(choice: string): string {
  return choice === 'ignore' ? 'Ignore' : kindLabel(choice)
}

const TRIAGED_LABELS: Record<string, string> = {
  task: 'Filed in My tasks',
  owe: 'Filed in I owe',
  waiting: 'Filed in Waiting on',
  fyi: 'Marked as FYI',
  ignore: 'Ignored',
}

/** What filing an inbox item did, for toasts and the history: "Filed in I owe", "Ignored". */
export function triagedLabel(choice: string): string {
  return TRIAGED_LABELS[choice] ?? 'Filed'
}

/** hugo's suggestion on an inbox row: "hugo: I owe · Fri 23 Oct" (the date asked for, if any). */
export function suggestionLabel(
  suggestion: Pick<ItemSuggestion, 'choice' | 'dueDate'> &
    Partial<Pick<ItemSuggestion, 'priority'>>,
  timeZone: string,
): string {
  const due = suggestion.dueDate ? ` · ${formatDueDate(suggestion.dueDate, timeZone)}` : ''
  const urgency =
    suggestion.priority === 'high' ? ' · High' : suggestion.priority === 'low' ? ' · Low' : ''
  return `hugo: ${choiceLabel(suggestion.choice)}${due}${urgency}`
}

/** "$0.00": AI cost in US dollars, two decimals. */
export function formatUsd(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

/**
 * Where an item is now, for its badge: the list while it is open, otherwise its state. An item
 * closed straight from the inbox has no list, so `kindLabel` alone would say "Inbox".
 */
export function itemStateLabel(item: {
  status: string
  kind: string | null
  closedReason: string | null
}): string {
  if (item.status === 'inbox') return 'Inbox'
  if (item.status === 'snoozed') return 'Snoozed'
  if (item.status === 'done') return item.kind === 'fyi' ? kindLabel('fyi') : 'Done'
  if (item.status === 'dropped') return item.closedReason === 'ignored' ? 'Ignored' : 'Dropped'
  return kindLabel(item.kind)
}

const SOURCE_LABELS: Record<string, string> = {
  hugo: 'hugo',
  gmail: 'Gmail',
  chat: 'Chat',
  calendar: 'Calendar',
  drive: 'Drive',
  github: 'GitHub',
  jira: 'Jira',
  confluence: 'Confluence',
  hubspot: 'HubSpot',
}

export function sourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? source
}

/** Due date in the user's time zone: "Fri 23 Oct" (all day) or "Thu 22 Oct, 15:00". */
export function formatDue(
  dueAt: string,
  allDay: boolean,
  timeZone: string,
  locale = 'en-GB',
): string {
  const date = new Date(dueAt)
  const day = new Intl.DateTimeFormat(locale, {
    timeZone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(date)
  if (allDay) return day.replace(',', '')
  const time = new Intl.DateTimeFormat(locale, {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
  return `${day.replace(',', '')}, ${time}`
}

/**
 * A day without a time (YYYY-MM-DD in the user's calendar, e.g. the date an ask states) like a
 * due badge: "Fri 23 Oct". Formatted at noon there, so the day is the same in every time zone.
 */
export function formatDueDate(date: string, timeZone: string, locale = 'en-GB'): string {
  const noon = zonedToUtc(parseLocalDate(date), 12 * 60, timeZone)
  return formatDue(new Date(noon).toISOString(), true, timeZone, locale)
}

const DAY_PARTS = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'UTC',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
})

/**
 * A calendar date (YYYY-MM-DD, e.g. a day off or the day work gets done) like a due badge:
 * "Mon 26 Oct", or "26 Oct" without the weekday. The year follows when it is not `thisYear`
 * ("Mon 4 Jan 2027"); leave `thisYear` out to never show it. The same day in every time zone.
 */
export function formatDay(
  date: string,
  thisYear: number | null = null,
  options: { weekday?: boolean } = {},
): string {
  const { year, month, day } = parseLocalDate(date)
  const parts = DAY_PARTS.formatToParts(new Date(Date.UTC(year, month - 1, day, 12)))
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? ''
  const text = `${part('day')} ${part('month')}`
  const withDay = options.weekday === false ? text : `${part('weekday')} ${text}`
  return thisYear === null || year === thisYear ? withDay : `${withDay} ${year}`
}

/**
 * Days from first to last, both included: "Mon 26 Oct – Fri 6 Nov" (or "26 Oct – 6 Nov" without
 * weekdays), one day alone when they are the same. A year is named once, on the last day, when
 * it is not `thisYear`; the first day names its own only when it differs from both.
 */
export function formatDayRange(
  startsOn: string,
  endsOn: string,
  thisYear: number,
  options: { weekday?: boolean } = {},
): string {
  if (startsOn === endsOn) return formatDay(startsOn, thisYear, options)
  const startYear = parseLocalDate(startsOn).year
  const endYear = parseLocalDate(endsOn).year
  // The first day leaves its year to the last day when both are in the same one.
  const start = formatDay(startsOn, startYear === endYear ? null : thisYear, options)
  return `${start} – ${formatDay(endsOn, thisYear, options)}`
}

/** Hours with up to one decimal, as the planning setting counts them: "0.5 h", "4 h", "12 h". */
export function formatHours(minutes: number): string {
  return `${Number((minutes / 60).toFixed(1))} h`
}

/** Time needed: "15 min", "1 h", "1 h 30 min", "40 h". */
export function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`
}

/** "YYYY-MM-DD" of an instant in a time zone (for date inputs). */
export function localDateInput(iso: string, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso))
  return parts
}

/** Short duration: "3d", "5h", "12m". */
export function shortAge(fromIso: string, now: Date): string {
  const ms = Math.max(0, now.getTime() - new Date(fromIso).getTime())
  if (ms < 3_600_000) return `${Math.max(1, Math.round(ms / 60_000))}m`
  if (ms < DAY_MS) return `${Math.round(ms / 3_600_000)}h`
  return `${Math.round(ms / DAY_MS)}d`
}

export function eventLabel(type: string): string {
  return EVENT_LABELS[type] ?? type
}

const OUTBOX_STATUS_LABELS: Record<OutboxStatus, string> = {
  pending: 'Waiting for approval',
  sending: 'Sending…',
  sent: 'Sent',
  dry_run: 'Would have been sent',
  failed: 'Failed',
  discarded: 'Discarded',
  withdrawn: 'Not sent',
}

/**
 * What happened to an outbox entry: "Waiting for approval", "Failed: <reason>", "Not sent: Marco
 * replied" (hugo withdrew it), …
 */
export function outboxStatusLabel(status: OutboxStatus, error: string | null = null): string {
  if (status === 'failed' && error) return `Failed: ${error}`
  if (status === 'withdrawn' && error) return `Not sent: ${error}`
  return OUTBOX_STATUS_LABELS[status]
}
